import { type PayoutCapability } from "@clipboard-health/contract-payment-service";
import { Box, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { LoadingSpinner, Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";
import { useCallback, useEffect, useMemo, useState } from "react";

import { type ExternalAccount, type PayoutSchedule } from "../api/schema";
import { resolvePreferredExternalAccount } from "../utils/resolvePreferredExternalAccount";
import { ExternalAccountItem } from "./ExternalAccountItem";
import { PayoutScheduleItem } from "./PayoutScheduleItem";
import { UpdateExternalAccountBottomSheet } from "./UpdateExternalAccountBottomSheet";

export interface PayoutSettingsChangeableSettings {
  selectedPayoutSchedule: PayoutSchedule;
  selectedPreferredExternalAccountStripeId: ExternalAccount["stripeId"];
}

export interface PayoutSettingsPageContentProps {
  payoutCapability: PayoutCapability;
  payoutSchedule: PayoutSchedule;
  preferredExternalAccountStripeId: ExternalAccount["stripeId"] | undefined;
  instantPayFee: number;
  externalAccounts: ExternalAccount[];
  isSaveLoading: boolean;
  isGoToStripeLoading: boolean;
  onDraftSettingsUpdated: (settings: PayoutSettingsChangeableSettings | undefined) => void;
  onSave: () => void;
  onGoToStripe: () => void;
}

export function PayoutSettingsPageContent(props: PayoutSettingsPageContentProps) {
  const {
    payoutCapability,
    payoutSchedule,
    preferredExternalAccountStripeId,
    instantPayFee,
    externalAccounts,
    isSaveLoading,
    isGoToStripeLoading,
    onDraftSettingsUpdated,
    onSave,
    onGoToStripe,
  } = props;
  const [selectedPayoutSchedule, setSelectedPayoutSchedule] = useState(payoutSchedule);
  const [selectedPreferredExternalAccountStripeId, setSelectedPreferredExternalAccountStripeId] =
    useState<ExternalAccount["stripeId"]>(
      () =>
        resolvePreferredExternalAccount({
          preferredExternalAccountStripeId,
          payoutSchedule,
          externalAccounts,
        }) ?? ""
    );

  const didChangeSetting =
    selectedPayoutSchedule !== payoutSchedule ||
    selectedPreferredExternalAccountStripeId !== preferredExternalAccountStripeId;

  useEffect(() => {
    onDraftSettingsUpdated(
      didChangeSetting
        ? { selectedPayoutSchedule, selectedPreferredExternalAccountStripeId }
        : undefined
    );
  }, [
    onDraftSettingsUpdated,
    didChangeSetting,
    selectedPayoutSchedule,
    selectedPreferredExternalAccountStripeId,
  ]);

  const updateExternalAccountBottomSheetModalState = useModalState();

  const instantExternalAccounts = useMemo(
    () => externalAccounts.filter((externalAccount) => externalAccount.supportsInstantPayouts),
    [externalAccounts]
  );
  const standardExternalAccounts = useMemo(
    () => externalAccounts.filter((externalAccount) => !externalAccount.supportsInstantPayouts),
    [externalAccounts]
  );

  const onSetSelectedPayoutSchedule = useCallback(
    (newPayoutSchedule: PayoutSchedule) => {
      if (newPayoutSchedule === "instant") {
        // we need to check if selected external account supports InstantPay
        const selectedExternalAccount = externalAccounts.find(
          (externalAccount) => externalAccount.stripeId === selectedPreferredExternalAccountStripeId
        );

        if (
          !selectedExternalAccount?.supportsInstantPayouts &&
          instantExternalAccounts.length > 0
        ) {
          // if selected external account does NOT support InstantPay
          // -> automatically select the first account that does
          setSelectedPreferredExternalAccountStripeId(instantExternalAccounts[0].stripeId);
        }
      }

      setSelectedPayoutSchedule(newPayoutSchedule);
    },
    [externalAccounts, instantExternalAccounts, selectedPreferredExternalAccountStripeId]
  );

  return (
    <>
      <Stack
        sx={(theme) => ({
          flex: 1,
          overflow: "hidden",
          ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
        })}
      >
        <Stack
          sx={(theme) => ({
            flex: 1,
            overflow: "auto",
            paddingX: theme.spacing(5),
            paddingTop: theme.spacing(5),
          })}
        >
          <Stack spacing={3}>
            <Text variant="h5" sx={{ fontWeight: 500 }}>
              Payout method
            </Text>
            <Text variant="body2">Select how you’d like to get paid</Text>
          </Stack>
          <Stack spacing={4} sx={(theme) => ({ marginTop: theme.spacing(4) })}>
            <PayoutScheduleItem
              payoutSchedule="instant"
              title="InstantPay"
              description="Clock out of your shift, and have your money in your bank account within 30 minutes."
              illustrationType="instant-pay-small"
              fee={instantPayFee}
              selected={selectedPayoutSchedule === "instant"}
              disabledMessage={
                payoutCapability === "instant"
                  ? undefined
                  : "None of your payout destinations support InstantPay."
              }
              disabledCTA={
                payoutCapability === "instant" ? undefined : "Add new payout destination"
              }
              onSelect={onSetSelectedPayoutSchedule}
              onDisabledCTAClick={() => {
                updateExternalAccountBottomSheetModalState.openModal();
              }}
            />
            <PayoutScheduleItem
              payoutSchedule="standard"
              title="Standard Payout"
              description="Your earnings will arrive in your bank account 1-3 business days after your completed shift."
              illustrationType="banknote-clock-small"
              fee={0}
              selected={selectedPayoutSchedule === "standard"}
              onSelect={onSetSelectedPayoutSchedule}
            />
          </Stack>
          <Stack spacing={3} sx={(theme) => ({ marginTop: theme.spacing(7) })}>
            <Text variant="h5" sx={{ fontWeight: 500 }}>
              Payout destination
            </Text>
            <Text variant="body2">Select where you’d like your payment to go</Text>
          </Stack>
          <Stack spacing={3} sx={(theme) => ({ marginTop: theme.spacing(4) })}>
            {instantExternalAccounts.map((externalAccount) => (
              <ExternalAccountItem
                key={externalAccount.stripeId}
                externalAccount={externalAccount}
                disabled={false}
                selected={externalAccount.stripeId === selectedPreferredExternalAccountStripeId}
                onSelect={setSelectedPreferredExternalAccountStripeId}
              />
            ))}
            {selectedPayoutSchedule === "instant" && standardExternalAccounts.length > 0 ? (
              <Text variant="body2" sx={(theme) => ({ marginTop: theme.spacing(3) })}>
                Only available with Standard Payout
              </Text>
            ) : null}
            {standardExternalAccounts.map((externalAccount) => (
              <ExternalAccountItem
                key={externalAccount.stripeId}
                externalAccount={externalAccount}
                disabled={selectedPayoutSchedule === "instant"}
                selected={externalAccount.stripeId === selectedPreferredExternalAccountStripeId}
                onSelect={setSelectedPreferredExternalAccountStripeId}
              />
            ))}
          </Stack>
          <TextButton
            sx={(theme) => ({ marginTop: theme.spacing(5), marginBottom: theme.spacing(4) })}
            onClick={() => {
              updateExternalAccountBottomSheetModalState.openModal();
            }}
          >
            Need to update your payout destinations?
          </TextButton>
        </Stack>
        <Box
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.border?.primary}`,
            paddingX: theme.spacing(5),
            paddingTop: theme.spacing(6),
            paddingBottom: theme.spacing(3),
          })}
        >
          <Button
            fullWidth
            aria-label="Save"
            disabled={isSaveLoading || !didChangeSetting}
            onClick={onSave}
          >
            {isSaveLoading && <LoadingSpinner size="small" />}
            {!isSaveLoading && !didChangeSetting && "Saved ✔️"}
            {!isSaveLoading && didChangeSetting && "Save"}
          </Button>
        </Box>
      </Stack>
      <UpdateExternalAccountBottomSheet
        modalState={updateExternalAccountBottomSheetModalState}
        isGoToStripeLoading={isGoToStripeLoading}
        onClose={updateExternalAccountBottomSheetModalState.closeModal}
        onGoToStripe={onGoToStripe}
      />
    </>
  );
}
